/**
 * Empresa e configuracoes: identidade, acesso, prazos por etapa e motivos
 * padronizados.
 *
 * Reexportado por `../resources.ts`. Ver docs/FRONTEND.md, secao 3.
 */

import { query } from './comum';

import { api } from '../client';

export interface TenantSettings {
  display_name: string;
  logo_url: string;
  logo_symbol_url: string;
  favicon_url: string;
  primary_color: string;
  accent_color: string;
  theme_color: string;
  background_color: string;
  custom_domain: string;
  pwa_name: string;
  pwa_short_name: string;
  locale: string;
  timezone: string;
  // Aba Acesso (P-ACE-1). Origem: configuracao do portal do OnPoint.
  login_title: string;
  login_subtitle_primary: string;
  login_subtitle_secondary: string;
  logo_white_url: string;
  login_gradient_enabled: boolean;
  login_gradient_colors: string[];
  login_background_url: string;
  broker_access_enabled: boolean;
  advisor_access_enabled: boolean;
  broker_self_registration_enabled: boolean;
  password_login_enabled: boolean;
  login_redirect_url: string;
  maintenance_warning_enabled: boolean;
  maintenance_message: string;
  support_url: string;
  public_login_enabled: boolean;
  /** Zero e desligado. */
  session_lock_idle_minutes: number;
  /**
   * Prazo de validade da senha em dias (R-1; COT-270, ESC-135, ESC-621). Nulo e
   * desligado; zero, enviado, desliga. O prazo do OnPoint nao tem fonte.
   */
  password_expiry_days?: number | null;
  /** Se a instalacao envia e-mail (o link de recuperacao de senha). So leitura. */
  email_delivery_available?: boolean;
  /**
   * Aviso do prazo da senha ligado sem envio de e-mail: como a senha vencida e
   * trocada. Vazio sem aviso. So leitura.
   */
  password_expiry_warning?: string;
  /**
   * Se a instalacao envia o link do autocadastro de corretora (R5-B19). So leitura.
   */
  broker_signup_email_delivery_available?: boolean;
  /**
   * Aviso do autocadastro ligado sem envio do link: o pedido fica registrado e nao
   * chega ao assistente. Vazio sem aviso. So leitura.
   */
  broker_self_registration_warning?: string;
  /**
   * Endereco publico do cadastro de corretores com acesso na hora (ADR 0095), como
   * `sudaseg` em seguracore.com.br/sudaseg. Vazio sem cadastro publico.
   */
  broker_signup_public_path?: string;
  /**
   * Aviso do endereco publico definido sem o envio de e-mail ligado na administracao
   * da plataforma: o cadastro publico fica indisponivel, porque sem e-mail nao ha
   * ativacao (ADR 0096). Vazio sem aviso. So leitura.
   */
  broker_signup_public_path_warning?: string;
}

export interface TenantDetail {
  id: string;
  slug: string;
  name: string;
  plan: string;
  status: string;
  settings: TenantSettings;
}

export const tenant = {
  current: (signal?: AbortSignal) =>
    api.get<TenantDetail>('/api/v1/tenants/current', signal),
  updateSettings: (input: Partial<TenantSettings>) =>
    api.patch<TenantSettings>('/api/v1/tenants/current/settings', input),
};

// --- Configuracoes da empresa -----------------------------------------------

/** Prazo acordado para uma etapa da esteira, em dias uteis. */
export interface StageSLA {
  stage: string;
  /** Zero significa etapa sem prazo definido, e nao prazo de zero dias. */
  business_days: number;
  updated_at: string;
}

/** Motivo padronizado, usado nas recusas e no cancelamento. */
export interface DeclineReason {
  id: string;
  scope: string;
  label: string;
  active: boolean;
  sort_order: number;
  /** Esteiras em que o motivo vale (P-EST-1). Opcional para respostas antigas. */
  pipe_ids?: string[];
}

export const settings = {
  stageSlas: (signal?: AbortSignal) =>
    api.get<StageSLA[]>('/api/v1/settings/stage-slas', signal),
  setStageSla: (stage: string, businessDays: number) =>
    api.put<StageSLA>(`/api/v1/settings/stage-slas/${stage}`, {
      business_days: businessDays,
    }),

  declineReasons: (
    params: { scope?: string; pipe_id?: string; include_inactive?: boolean } = {},
    signal?: AbortSignal,
  ) =>
    api.get<DeclineReason[]>(
      `/api/v1/settings/decline-reasons${query(params)}`,
      signal,
    ),
  createReason: (input: {
    scope: string;
    label: string;
    sort_order?: number;
    pipe_ids?: string[];
  }) => api.post<DeclineReason>('/api/v1/settings/decline-reasons', input),
  updateReason: (
    id: string,
    input: {
      label?: string;
      active?: boolean;
      sort_order?: number;
      pipe_ids?: string[];
    },
  ) => api.patch<DeclineReason>(`/api/v1/settings/decline-reasons/${id}`, input),
  removeReason: (id: string) =>
    api.delete<void>(`/api/v1/settings/decline-reasons/${id}`),
};

// --- Meios de pagamento da empresa (P-TOM-C1) ---------------------------------

/**
 * Meio de pagamento da lista da empresa, oferecido na conta bancaria do tomador.
 * No OnPoint a lista e da empresa (Core/GetPaymentMethod) e veio vazia: aqui ela
 * nasce vazia e a empresa a preenche.
 */
export interface PaymentMethod {
  id: string;
  label: string;
  active: boolean;
  sort_order: number;
}

export const paymentMethods = {
  list: (params: { include_inactive?: boolean } = {}, signal?: AbortSignal) =>
    api.get<PaymentMethod[]>(
      `/api/v1/settings/payment-methods${query(params)}`,
      signal,
    ),
  create: (input: { label: string; sort_order?: number }) =>
    api.post<PaymentMethod>('/api/v1/settings/payment-methods', input),
  update: (
    id: string,
    input: { label?: string; active?: boolean; sort_order?: number },
  ) => api.patch<PaymentMethod>(`/api/v1/settings/payment-methods/${id}`, input),
  remove: (id: string) => api.delete<void>(`/api/v1/settings/payment-methods/${id}`),
};

// --- Calendario da empresa: horario comercial e feriados (P-BI-1) --------------

/** Horario comercial, em minutos desde a meia-noite (540 = 9h). */
export interface BusinessHours {
  start_minute: number;
  end_minute: number;
}

/** Feriado da empresa: um dia civil, sem hora. */
export interface BusinessHoliday {
  id: string;
  date: string;
  name: string;
  created_label: string;
  created_at: string;
}

export const businessCalendar = {
  hours: (signal?: AbortSignal) =>
    api.get<BusinessHours>('/api/v1/settings/business-hours', signal),
  setHours: (input: BusinessHours) =>
    api.put<BusinessHours>('/api/v1/settings/business-hours', input),
  holidays: (year: number | undefined, signal?: AbortSignal) =>
    api.get<BusinessHoliday[]>(
      `/api/v1/settings/business-holidays${year ? `?year=${year}` : ''}`,
      signal,
    ),
  createHoliday: (input: { date: string; name: string }) =>
    api.post<BusinessHoliday>('/api/v1/settings/business-holidays', input),
  removeHoliday: (id: string) =>
    api.delete<void>(`/api/v1/settings/business-holidays/${id}`),
};
